/**
 * Delivery zones (F-FE-N26) — shared types + geometry for the admin editor.
 *
 * The zone list is stored whole in app_settings.delivery_zones (the
 * enabled_order_types pattern). A zone's coverage is a set of named rings
 * (`polygons`): areas picked from the predefined library (lib/areaLibrary.ts)
 * plus freehand-drawn shapes. Matching is polygon-based; the `areas` strings
 * remain plain customer-facing labels on the wire (the editor keeps them in
 * sync with ring names).
 *
 * pointInRing mirrors backend/internal/geo/geo.go EXACTLY (edge/vertex counts
 * as inside; winding-agnostic). The server is authoritative — this copy only
 * powers instant editor/storefront feedback. Test vectors are pinned in
 * backend delivery_zones_test.go, storefront geo.test.ts, and
 * deliveryZones.test.ts here; change all together.
 */

export interface LatLng {
  lat: number
  lng: number
}

/**
 * One named ring of a zone's coverage. `library` rings come from the
 * predefined area library (slug = library area id); `drawn` rings are
 * freehand. Only `ring` is used for matching — slug/name/source are editor
 * metadata.
 */
export interface ZonePolygon {
  slug: string // '' for drawn
  name: string // "F-10" | "Custom area 1"
  source: 'library' | 'drawn'
  ring: LatLng[]
}

export interface DeliveryZone {
  id: string
  name: string
  color: string
  areas: string[]
  min_order: number
  delivery_charge: number
  rider_charge: number
  eta_minutes: number
  enabled: boolean
  sort_order: number
  polygons: ZonePolygon[]
}

/** A zone's matchable rings (drops degenerate <3-vertex rings). */
export function zoneRings(z: Pick<DeliveryZone, 'polygons'>): LatLng[][] {
  return z.polygons.filter((p) => p.ring.length >= 3).map((p) => p.ring)
}

/** Distinct, map-legible zone colors (rotates as zones are added). */
export const ZONE_COLORS = [
  '#e11d48', // rose
  '#2563eb', // blue
  '#16a34a', // green
  '#9333ea', // purple
  '#ea580c', // orange
  '#0d9488', // teal
  '#ca8a04', // amber
  '#db2777', // pink
] as const

export function nextZoneColor(existing: DeliveryZone[]): string {
  const used = new Set(existing.map((z) => z.color))
  return ZONE_COLORS.find((c) => !used.has(c)) ?? ZONE_COLORS[existing.length % ZONE_COLORS.length]
}

export function newDeliveryZone(existing: DeliveryZone[], polygons: ZonePolygon[]): DeliveryZone {
  return {
    id: newZoneId(),
    name: `Zone ${existing.length + 1}`,
    color: nextZoneColor(existing),
    areas: polygons.map((p) => p.name).filter(Boolean),
    min_order: 0,
    delivery_charge: 0,
    rider_charge: 0,
    eta_minutes: 45,
    enabled: true,
    sort_order: existing.length,
    polygons,
  }
}

/** A freehand ring wrapped as a drawn ZonePolygon with the next free label. */
export function drawnZonePolygon(zone: Pick<DeliveryZone, 'polygons'> | null, ring: LatLng[]): ZonePolygon {
  const used = new Set(zone?.polygons.map((p) => p.name) ?? [])
  let n = 1
  while (used.has(`Custom area ${n}`)) n += 1
  return { slug: '', name: `Custom area ${n}`, source: 'drawn', ring }
}

// crypto.randomUUID is missing on older Node (vitest on Node 18); zone ids
// only need uniqueness within one tenant's zone list.
const newZoneId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `z-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

const isLatLng = (p: unknown): p is LatLng =>
  !!p && typeof p === 'object' && typeof (p as LatLng).lat === 'number' && typeof (p as LatLng).lng === 'number'

/**
 * Tolerant normalizer for the stored JSON (missing fields get defaults).
 * Zones saved before the area library carried a single legacy `polygon` ring —
 * it lifts into one drawn ZonePolygon; the editor saves v2 (`polygons`) only.
 */
export function normalizeDeliveryZones(raw: unknown): DeliveryZone[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((z): z is Record<string, unknown> => !!z && typeof z === 'object')
    .map((z, i) => {
      let polygons: ZonePolygon[] = Array.isArray(z.polygons)
        ? z.polygons
            .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object' && Array.isArray((p as { ring?: unknown }).ring))
            .map((p, j) => ({
              slug: typeof p.slug === 'string' ? p.slug : '',
              name: typeof p.name === 'string' && p.name ? p.name : `Area ${j + 1}`,
              source: (p.source === 'library' ? 'library' : 'drawn') as ZonePolygon['source'],
              ring: (p.ring as unknown[]).filter(isLatLng),
            }))
            .filter((p) => p.ring.length >= 3)
        : []
      if (polygons.length === 0 && Array.isArray(z.polygon)) {
        const ring = z.polygon.filter(isLatLng)
        if (ring.length >= 3) polygons = [{ slug: '', name: 'Drawn area', source: 'drawn', ring }]
      }
      return {
        id: typeof z.id === 'string' && z.id ? z.id : newZoneId(),
        name: typeof z.name === 'string' ? z.name : `Zone ${i + 1}`,
        color: typeof z.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(z.color) ? z.color : ZONE_COLORS[i % ZONE_COLORS.length],
        areas: Array.isArray(z.areas) ? z.areas.filter((a): a is string => typeof a === 'string') : [],
        min_order: typeof z.min_order === 'number' && z.min_order >= 0 ? z.min_order : 0,
        delivery_charge: typeof z.delivery_charge === 'number' && z.delivery_charge >= 0 ? z.delivery_charge : 0,
        rider_charge: typeof z.rider_charge === 'number' && z.rider_charge >= 0 ? z.rider_charge : 0,
        eta_minutes: typeof z.eta_minutes === 'number' && z.eta_minutes >= 0 ? Math.min(z.eta_minutes, 1440) : 0,
        enabled: z.enabled !== false,
        sort_order: typeof z.sort_order === 'number' ? z.sort_order : i,
        polygons,
      }
    })
}

/** Ray-casting point-in-polygon; mirrors backend/internal/geo/geo.go. */
export function pointInRing(p: LatLng, ring: LatLng[]): boolean {
  const n = ring.length
  if (n < 3) return false
  let inside = false
  for (let i = 0, j = n - 1; i < n; j = i, i += 1) {
    const a = ring[j]
    const b = ring[i]
    if (onSegment(p, a, b)) return true
    if (a.lat > p.lat !== b.lat > p.lat) {
      const x = a.lng + ((p.lat - a.lat) * (b.lng - a.lng)) / (b.lat - a.lat)
      if (p.lng < x) inside = !inside
    }
  }
  return inside
}

const EPS = 1e-9

function onSegment(p: LatLng, a: LatLng, b: LatLng): boolean {
  const cross = (b.lng - a.lng) * (p.lat - a.lat) - (b.lat - a.lat) * (p.lng - a.lng)
  if (cross > EPS || cross < -EPS) return false
  if (p.lat < Math.min(a.lat, b.lat) - EPS || p.lat > Math.max(a.lat, b.lat) + EPS) return false
  if (p.lng < Math.min(a.lng, b.lng) - EPS || p.lng > Math.max(a.lng, b.lng) + EPS) return false
  return true
}

/** Absolute planar ring area (shoelace); only used to rank overlapping zones. */
export function ringArea(ring: LatLng[]): number {
  const n = ring.length
  if (n < 3) return 0
  let sum = 0
  for (let i = 0, j = n - 1; i < n; j = i, i += 1) {
    sum += (ring[j].lng + ring[i].lng) * (ring[j].lat - ring[i].lat)
  }
  return Math.abs(sum) / 2
}

/**
 * The zone containing the point — a zone matches when ANY of its rings
 * contains it; smallest total coverage (sum of ring areas) wins on overlap,
 * tie-broken by sort_order (same rule as backend matchDeliveryZone).
 */
export function matchZone<T extends Pick<DeliveryZone, 'polygons' | 'sort_order'> & { enabled?: boolean }>(
  zones: T[],
  p: LatLng,
): T | null {
  let best: T | null = null
  let bestArea = 0
  for (const z of zones) {
    if (z.enabled === false) continue
    let inside = false
    let area = 0
    for (const ring of zoneRings(z)) {
      if (!inside && pointInRing(p, ring)) inside = true
      area += ringArea(ring)
    }
    if (!inside) continue
    if (!best || area < bestArea || (area === bestArea && z.sort_order < best.sort_order)) {
      best = z
      bestArea = area
    }
  }
  return best
}
